import { ref, readonly, computed } from "vue";
import { useRouter } from "vue-router";
import { authService } from "@/services/authService";
import { passkeyService } from "@/services/passkeyService";
import { useSdk } from "@/composables/useSdk";
import type { AuthUser } from "@/types";

/**
 * Get display handle for @username display. Uses username when present,
 * otherwise derives from name or email when username is empty (stale cache, pre-setup).
 */
export function getDisplayHandle(
  user: { username?: string; name?: string; displayName?: string; email?: string } | null | undefined
): string {
  if (!user) return "unknown";
  const u = user.username?.trim();
  if (u) return u;
  const name = (user.name || user.displayName)?.trim();
  if (name) {
    const slug = name.toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9-_]/g, "");
    if (slug) return slug;
  }
  const email = user.email?.trim();
  if (email?.includes("@")) {
    const local = email.split("@")[0]?.trim();
    if (local) return local;
  }
  return "unknown";
}

/**
 * Check if user has a specific badge
 * Handles both string[] and Badge[] (objects with name property)
 * Exported for use in components that need custom badge checks
 */
export function hasBadge(badges: readonly unknown[] | unknown[] | undefined, badgeName: string): boolean {
  if (!badges || !Array.isArray(badges)) {
    return false;
  }
  return badges.some((badge) => {
    if (typeof badge === "string") {
      return badge.toLowerCase() === badgeName.toLowerCase();
    }
    if (badge && typeof badge === "object" && "name" in badge) {
      return (badge as { name: string }).name.toLowerCase() === badgeName.toLowerCase();
    }
    return false;
  });
}

// Reactive auth state (singleton)
// We restore state from localStorage on module load to survive HMR
const storedUser = authService.getStoredUser();
const hasToken = authService.isAuthenticated();

const user = ref<AuthUser | null>(storedUser);
const isLoading = ref(false);
const error = ref<string | null>(null);
// Mark as initialized if we have a token (state restored from storage)
const isInitialized = ref(hasToken);

/**
 * Composable for authentication state and actions
 */
export function useAuth() {
  const router = useRouter();
  const { setTokens: setSdkTokens } = useSdk();

  // Computed properties
  const isAuthenticated = computed(() => Boolean(user.value));
  const isAdmin = computed(() => hasBadge(user.value?.badges, "administrator"));

  /**
   * Check if the current user has a specific badge (case-insensitive).
   * Badge names match v1/settings badges (e.g., "Beta Tester", "Content Creator").
   */
  function currentUserHasBadge(badgeName: string): boolean {
    return hasBadge(user.value?.badges, badgeName);
  }

  /**
   * Initialize auth state from storage
   * Called once on app startup
   */
  async function initialize(): Promise<void> {
    // If already initialized with valid user, just ensure SDK has tokens
    if (isInitialized.value && user.value) {
      const tokens = authService.getTokens();
      if (tokens?.accessToken) {
        setSdkTokens(tokens);
      }
      return;
    }

    isLoading.value = true;
    error.value = null;

    try {
      const { tokens, user: storedUser } = authService.initializeFromStorage();

      if (tokens?.accessToken) {
        // Set tokens in SDK
        setSdkTokens(tokens);

        // Use cached user for instant UI, then always fetch fresh data (roles, badges, etc.)
        if (storedUser) {
          user.value = storedUser;
        }
        // Always fetch fresh user from API to get roles, badges, and other server state
        try {
          await fetchCurrentUser();
        } catch (err) {
          console.warn("User refresh failed, using cached user:", err);
        }
      }
    } catch (err) {
      console.error("Auth initialization error:", err);
      error.value =
        err instanceof Error ? err.message : "Failed to initialize auth";
    } finally {
      isLoading.value = false;
      isInitialized.value = true;
    }
  }

  /**
   * Send magic link to email
   */
  async function sendMagicLink(
    email: string,
    options?: {
      referralCode?: string | null;
      redirect?: string | null;
      platform?: string;
    },
  ): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      await authService.sendMagicLink(email, options);
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to send magic link";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Send auth code to email (alias for sendMagicLink with cleaner name)
   */
  async function sendAuthCode(
    email: string,
    options?: {
      referralCode?: string | null;
      redirect?: string | null;
      platform?: string;
    },
  ): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      await authService.sendAuthCode(email, options);
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to send auth code";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Verify the auth code from magic link
   */
  async function verifyCode(email: string, code: string): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      const { tokens, user: authUser } = await authService.verifyCode(
        email,
        code,
      );

      // Set tokens in SDK
      setSdkTokens(tokens);

      // Set user state
      user.value = authUser;

      // Navigate to redirect URL or home
      const redirect = router.currentRoute.value.query.redirect as string;
      await router.push(redirect || "/");
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to verify code";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Login with passkey (WebAuthn)
   * @param email - Optional email to limit which passkeys are accepted
   */
  async function loginWithPasskey(email?: string): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      const { tokens, user: authUser } = await passkeyService.authenticateWithPasskey(email);

      // Set tokens in SDK
      setSdkTokens(tokens);

      // Set user state
      user.value = authUser;

      // Navigate to redirect URL or home
      const redirect = router.currentRoute.value.query.redirect as string;
      await router.push(redirect || "/");
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to login with passkey";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Login with OAuth provider (Google, Apple, etc.)
   * @param provider - OAuth provider name (e.g., 'google', 'apple')
   * @param code - Authorization code from OAuth callback
   * @param redirectUri - The redirect URI used in the OAuth flow (defaults to current origin + callback path)
   * @param extraData - Additional data for provider-specific needs (e.g., Apple's id_token and user info)
   */
  async function loginWithOAuth(
    provider: string,
    code: string,
    redirectUri?: string,
    extraData?: { id_token?: string | null; user?: string | null },
  ): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      // Default redirect URI is the callback path on current origin
      const uri = redirectUri || `${window.location.origin}/auth/${provider}/callback`;

      const { tokens, user: authUser } = await authService.loginWithOAuth(
        provider,
        code,
        uri,
        extraData,
      );

      // Set tokens in SDK
      setSdkTokens(tokens);

      // Set user state
      user.value = authUser;

      // Navigate to redirect URL or home
      const redirect = router.currentRoute.value.query.redirect as string;
      await router.push(redirect || "/");
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to login with OAuth";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Set a developer token directly (for testing)
   */
  async function setDeveloperToken(token: string): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      // Set token in auth service and SDK
      authService.setDeveloperToken(token);

      // Set token in SDK
      setSdkTokens({ accessToken: token });

      // Fetch user profile with the new token
      await fetchCurrentUser();

      // Navigate to redirect URL or home
      const redirect = router.currentRoute.value.query.redirect as string;
      await router.push(redirect || "/");
    } catch (err) {
      error.value =
        err instanceof Error ? err.message : "Failed to set developer token";
      // Clear the invalid token
      await authService.logout();
      setSdkTokens(null);
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Fetch current user profile
   */
  async function fetchCurrentUser(): Promise<void> {
    try {
      const authUser = await authService.fetchCurrentUser();
      user.value = authUser;
    } catch (err) {
      console.error("Failed to fetch user:", err);
      // If fetch fails, clear auth state
      logout();
      throw err;
    }
  }

  /**
   * Refresh current user profile (alias for fetchCurrentUser but doesn't logout on error)
   */
  async function refreshUser(): Promise<void> {
    try {
      const authUser = await authService.fetchCurrentUser();
      user.value = authUser;
    } catch (err) {
      console.error("Failed to refresh user:", err);
      throw err;
    }
  }

  /**
   * Logout and clear all auth state
   */
  async function logout(): Promise<void> {
    await authService.logout();
    setSdkTokens(null);
    user.value = null;
    error.value = null;
    router.push("/login");
  }

  /**
   * Clear current error
   */
  function clearError(): void {
    error.value = null;
  }

  /**
   * Set user data directly (used for read-after-write pattern)
   * Updates both the reactive state and localStorage
   */
  function setUser(userData: AuthUser): void {
    user.value = userData;
    authService.storeUser(userData);
  }

  return {
    // State (readonly)
    user: readonly(user),
    isLoading: readonly(isLoading),
    error: readonly(error),
    isInitialized: readonly(isInitialized),
    isAuthenticated,
    isAdmin,
    currentUserHasBadge,
    getDisplayHandle,

    // Actions
    initialize,
    sendMagicLink,
    sendAuthCode,
    verifyCode,
    loginWithPasskey,
    loginWithOAuth,
    setDeveloperToken,
    fetchCurrentUser,
    refreshUser,
    setUser,
    logout,
    clearError,
  };
}

// Export type for the composable return value
export type UseAuthReturn = ReturnType<typeof useAuth>;
